import { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import type { VideoClip } from '@/types/api'
import { cn } from '@/lib/utils'

/**
 * A responsive grid of MP4 video clips from soccerclipboards.com.
 * Featured ones can autoplay muted; others need a click.
 */
export function VideoReel({ videos, layout = 'grid' }: { videos: VideoClip[]; layout?: 'grid' | 'row' }) {
  return (
    <div className={cn(
      'grid gap-4',
      layout === 'row' ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-3'
    )}>
      {videos.map((v, i) => (
        <motion.div
          key={v.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ delay: i * 0.08, duration: 0.5 }}
        >
          <VideoCard clip={v} />
        </motion.div>
      ))}
    </div>
  )
}

/** Single video with custom play/mute controls overlay. */
function VideoCard({ clip }: { clip: VideoClip }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(clip.autoplay)
  const [muted, setMuted] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (clip.autoplay) {
      // browsers block muted autoplay differently; try, then fall back to paused state
      el.play().catch(() => setPlaying(false))
    }
  }, [clip.autoplay])

  function toggle() {
    const el = ref.current
    if (!el) return
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {})
    } else {
      el.pause()
      setPlaying(false)
    }
  }
  function toggleMute() {
    const el = ref.current
    if (!el) return
    const next = !el.muted
    el.muted = next
    setMuted(next)
  }

  return (
    <div className="group relative rounded-2xl overflow-hidden border border-white/10 bg-black shadow-xl">
      <div className="aspect-video relative bg-pitch-950">
        {!failed ? (
          <video
            ref={ref}
            className="absolute inset-0 w-full h-full object-cover"
            src={clip.src}
            poster={clip.poster}
            playsInline
            muted={muted}
            loop={clip.loop}
            preload="metadata"
            controlsList="nodownload"
            onError={() => setFailed(true)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
        ) : (
          <img src={clip.poster} alt={clip.title} className="absolute inset-0 w-full h-full object-cover" />
        )}

        {/* Overlay gradient */}
        <div className={cn(
          'absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent transition-opacity pointer-events-none',
          playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
        )} />

        {/* Play/Pause big center button */}
        {!playing && !failed && (
          <button
            onClick={toggle}
            aria-label="Play video"
            className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/50 transition-colors"
          >
            <div className="grid h-20 w-20 place-items-center rounded-full bg-cosmos-gold text-pitch-950 shadow-2xl group-hover:scale-110 transition-transform">
              <Play className="h-8 w-8 fill-current translate-x-0.5" />
            </div>
          </button>
        )}

        {/* Corner controls when playing */}
        {playing && (
          <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={toggle}
              aria-label="Pause video"
              className="grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white hover:bg-cosmos-gold hover:text-pitch-950"
            >
              <Pause className="h-4 w-4" />
            </button>
            <button
              onClick={toggleMute}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white hover:bg-cosmos-gold hover:text-pitch-950"
            >
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
          </div>
        )}

        {/* Title bar */}
        <div className="absolute bottom-3 left-3 right-3 text-white pointer-events-none">
          <div className="font-display text-base md:text-lg uppercase leading-tight tracking-wide">{clip.title}</div>
          <div className="text-[11px] text-white/70 mt-0.5 line-clamp-1">{clip.description}</div>
        </div>
      </div>
    </div>
  )
}
